import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";
import ViewerNavbar from "./ViewerNavbar";
import "./DvViewer.css";

/**
 * EHR Viewer Page Component
 *
 * Standalone page for viewing Electronic Health Records data.
 * Supports JSON files with nested objects and arrays.
 */
const EHRViewerPage = () => {
    const navigate = useNavigate();
    const urlParams = new URLSearchParams(window.location.search);
    const studyId = urlParams.get('study_id');
    const patientIdsParam = urlParams.get('patient_ids');
    const ehrDataParam = urlParams.get('ehr_data');

    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);

    const [ehrRecords, setEhrRecords] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("structured");

    // Fetch study info
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;
        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name);
            setLabels(response.data.labels || []);
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);
        } catch (err) {
            console.error("Error fetching study info:", err);
        }
    }, [studyId]);

    // Fetch EHR data
    const fetchEHRData = useCallback(async () => {
        console.log("[EHR Viewer] ehrDataParam:", ehrDataParam);
        console.log("[EHR Viewer] studyId:", studyId);
        console.log("[EHR Viewer] patientIdsParam:", patientIdsParam);

        if (!ehrDataParam) {
            console.log("[EHR Viewer] No ehrDataParam found, stopping fetch");
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Parse EHR data param: "id:source_model,id:source_model,..."
            const ehrItems = ehrDataParam.split(',').map(item => {
                const [id, sourceModel] = item.split(':');
                return { id, sourceModel: sourceModel || 'PatientData' };
            });
            console.log("[EHR Viewer] Parsed EHR items:", ehrItems);

            const records = [];
            for (const item of ehrItems) {
                try {
                    // Fetch from PatientData endpoint (data_manager app)
                    console.log(`Fetching EHR record ${item.id}...`);
                    const response = await API.get(`/data_manager/patient-data/${item.id}/`);
                    const data = response.data;
                    console.log("EHR record data:", data);

                    // Try to parse JSON/CSV content from file
                    // Note: metadata field often contains upload tracking info, not EHR data
                    // So we prioritize fetching the actual file content
                    let content = {};

                    if (data.file) {
                        try {
                            // Use the API content endpoint to fetch file content
                            // This handles authentication and file access properly
                            console.log("Fetching EHR file content via API for record:", item.id);

                            const contentResponse = await API.get(`/data_manager/patient-data/${item.id}/content/`);
                            const text = contentResponse.data.content;

                            // Try to parse as JSON first
                            try {
                                content = JSON.parse(text);
                            } catch (jsonErr) {
                                // If not JSON, check if it's CSV
                                if (data.original_file_type === 'csv' || data.filename?.toLowerCase().endsWith('.csv')) {
                                    // Simple CSV to object conversion (first row as headers)
                                    const lines = text.trim().split('\n');
                                    if (lines.length > 0) {
                                        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
                                        const rows = [];
                                        for (let i = 1; i < lines.length; i++) {
                                            const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                                            const row = {};
                                            headers.forEach((h, idx) => {
                                                row[h] = values[idx] || '';
                                            });
                                            rows.push(row);
                                        }
                                        content = { headers, rows, _format: 'csv' };
                                    }
                                } else {
                                    // Store raw text content
                                    content = { raw_content: text, _format: 'text' };
                                }
                            }
                        } catch (parseErr) {
                            console.warn("Could not parse EHR file:", parseErr);
                            content = { error: `Could not load file: ${parseErr.message}` };
                        }
                    }

                    records.push({
                        id: item.id,
                        patient_id: data.patient_id,
                        filename: data.filename,
                        uploaded_at: data.uploaded_at,
                        description: data.description,
                        content: content
                    });
                } catch (err) {
                    console.error(`Error fetching EHR record ${item.id}:`, err);
                }
            }

            setEhrRecords(records);
            if (records.length > 0) {
                setSelectedRecord(records[0]);
            }
        } catch (err) {
            console.error("Error fetching EHR data:", err);
            toast.error("Failed to load EHR data");
        } finally {
            setLoading(false);
        }
    }, [ehrDataParam]);

    useEffect(() => {
        fetchStudyInfo();
        fetchEHRData();
    }, [fetchStudyInfo, fetchEHRData]);

    const toggleSection = (key) => {
        setExpandedSections(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const expandAll = () => {
        if (!selectedRecord?.content) return;
        const allExpanded = {};
        const setAllExpanded = (obj, prefix = '') => {
            if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    const fullKey = prefix ? `${prefix}.${key}` : key;
                    allExpanded[fullKey] = true;
                    if (typeof obj[key] === 'object') {
                        setAllExpanded(obj[key], fullKey);
                    }
                });
            }
        };
        setAllExpanded(selectedRecord.content);
        setExpandedSections(allExpanded);
    };

    const collapseAll = () => {
        setExpandedSections({});
    };

    const formatValue = (value) => {
        if (value === null) return <span className="ehr-null">null</span>;
        if (value === undefined) return <span className="ehr-undefined">undefined</span>;
        if (typeof value === 'boolean') return <span className={`ehr-boolean ${value ? 'true' : 'false'}`}>{value.toString()}</span>;
        if (typeof value === 'number') return <span className="ehr-number">{value}</span>;
        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                return <span className="ehr-date">{value}</span>;
            }
            if (value.startsWith('http://') || value.startsWith('https://')) {
                return <a href={value} target="_blank" rel="noopener noreferrer" className="ehr-url">{value}</a>;
            }
            return <span className="ehr-string">"{value}"</span>;
        }
        return String(value);
    };

    const renderValue = (key, value, depth = 0, parentKey = '') => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        const isExpanded = expandedSections[fullKey] ?? depth < 2;

        const matchesSearch = !searchTerm ||
            key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase()));

        if (typeof value === 'object' && value !== null) {
            const isArray = Array.isArray(value);
            const itemCount = isArray ? value.length : Object.keys(value).length;
            const hasMatchingChild = searchTerm && JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch && !hasMatchingChild && searchTerm) {
                return null;
            }

            return (
                <div key={fullKey} className={`ehr-item ehr-object depth-${Math.min(depth, 4)}`}>
                    <div className="ehr-key-row" onClick={() => toggleSection(fullKey)}>
                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} ehr-chevron`}></i>
                        <span className="ehr-key">{key}</span>
                        <span className="ehr-type">
                            {isArray ? `Array[${itemCount}]` : `Object{${itemCount}}`}
                        </span>
                    </div>
                    {isExpanded && (
                        <div className="ehr-nested">
                            {isArray ? (
                                value.map((item, index) => renderValue(index, item, depth + 1, fullKey))
                            ) : (
                                Object.entries(value).map(([k, v]) => renderValue(k, v, depth + 1, fullKey))
                            )}
                        </div>
                    )}
                </div>
            );
        }

        if (!matchesSearch && searchTerm) {
            return null;
        }

        return (
            <div key={fullKey} className={`ehr-item ehr-primitive depth-${Math.min(depth, 4)}`}>
                <span className="ehr-key">{key}:</span>
                {formatValue(value)}
            </div>
        );
    };

    const content = selectedRecord?.content || {};

    return (
        <div className="dv-viewer-page ehr-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                questions={questions}
                scores={scores}
                patientId={patientIdsParam?.split(',')[0]}
                shareAnnotations={shareAnnotations}
            />

            <div className="dv-viewer-content">
                <div className="ehr-page-layout">
                    {/* Left Sidebar - Record List */}
                    <div className="ehr-sidebar">
                        <div className="ehr-sidebar-header">
                            <i className="fa fa-heartbeat me-2"></i>
                            <span>EHR Records</span>
                            <span className="badge bg-primary ms-2">{ehrRecords.length}</span>
                        </div>
                        <div className="ehr-record-list">
                            {loading ? (
                                <div className="ehr-loading">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span className="ms-2">Loading records...</span>
                                </div>
                            ) : ehrRecords.length === 0 ? (
                                <div className="ehr-empty-sidebar">
                                    <i className="fa fa-file-text-o"></i>
                                    <p>No EHR records found</p>
                                </div>
                            ) : (
                                ehrRecords.map(record => (
                                    <div
                                        key={record.id}
                                        className={`ehr-record-item ${selectedRecord?.id === record.id ? 'active' : ''}`}
                                        onClick={() => setSelectedRecord(record)}
                                    >
                                        <div className="ehr-record-icon">
                                            <i className="fa fa-file-text"></i>
                                        </div>
                                        <div className="ehr-record-info">
                                            <div className="ehr-record-name">{record.filename || `Record ${record.id}`}</div>
                                            <div className="ehr-record-patient">
                                                <i className="fa fa-user me-1"></i>
                                                {record.patient_id}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Main Content - EHR Viewer */}
                    <div className="ehr-main-content">
                        {!selectedRecord ? (
                            <div className="ehr-empty-state">
                                <i className="fa fa-heartbeat"></i>
                                <h3>No Record Selected</h3>
                                <p>Select an EHR record from the sidebar to view its contents.</p>
                            </div>
                        ) : (
                            <>
                                {/* Toolbar */}
                                <div className="ehr-toolbar">
                                    <div className="ehr-toolbar-left">
                                        <h4 className="ehr-record-title">
                                            <i className="fa fa-heartbeat me-2"></i>
                                            {selectedRecord.filename || `EHR Record`}
                                        </h4>
                                        {selectedRecord.patient_id && (
                                            <span className="ehr-patient-badge">
                                                <i className="fa fa-user me-1"></i>
                                                {selectedRecord.patient_id}
                                            </span>
                                        )}
                                    </div>
                                    <div className="ehr-toolbar-right">
                                        <div className="ehr-search">
                                            <i className="fa fa-search"></i>
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            {searchTerm && (
                                                <button className="ehr-clear-search" onClick={() => setSearchTerm('')}>
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            )}
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className={`btn ${viewMode === 'structured' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setViewMode('structured')}
                                            >
                                                <i className="fa fa-sitemap me-1"></i> Tree
                                            </button>
                                            <button
                                                className={`btn ${viewMode === 'raw' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                onClick={() => setViewMode('raw')}
                                            >
                                                <i className="fa fa-code me-1"></i> JSON
                                            </button>
                                        </div>
                                        {viewMode === 'structured' && (
                                            <div className="btn-group btn-group-sm ms-2">
                                                <button className="btn btn-outline-secondary" onClick={expandAll}>
                                                    <i className="fa fa-expand"></i>
                                                </button>
                                                <button className="btn btn-outline-secondary" onClick={collapseAll}>
                                                    <i className="fa fa-compress"></i>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Content Area */}
                                <div className="ehr-content-area">
                                    {viewMode === 'structured' ? (
                                        <div className="ehr-structured-view">
                                            {Object.keys(content).length === 0 ? (
                                                <div className="ehr-empty">
                                                    <i className="fa fa-info-circle"></i>
                                                    <p>No data available in this record</p>
                                                </div>
                                            ) : (
                                                Object.entries(content).map(([key, value]) =>
                                                    renderValue(key, value, 0, '')
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <div className="ehr-raw-view">
                                            <pre>{JSON.stringify(content, null, 2)}</pre>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EHRViewerPage;
